import { JobStatusType, ProcessingJobStatus } from 'features/jobs/types'
import { checkProcessingJob } from 'features/jobs/api/processingJobs'
import { usePolling, createRetryOnServerError } from 'features/common/hooks/usePolling'

const POLLING_INTERVAL = 1500 // 1.5 seconds
export const JOB_STATUS_QUERY_KEY = 'jobStatus'

export function useMultipleJobStatusPolling({
  jobs,
  enabled = true
}: {
  jobs: Array<{ connectionName: string; jobId: string }>
  enabled?: boolean
}) {
  const { results, hasError, isLoading, refetchAll } = usePolling({
    queries: jobs.map((job) => ({
      queryKey: [JOB_STATUS_QUERY_KEY, job.connectionName, job.jobId],
      queryFn: () => checkProcessingJob({ connection: job.connectionName, jobId: job.jobId }),
      enabled: enabled && !!job.connectionName && !!job.jobId,
      refetchInterval: (data: ProcessingJobStatus | undefined) => {
        // Stop polling if job is done or error
        if (
          data?.status === JobStatusType.Success ||
          data?.status === JobStatusType.Failure ||
          data?.status === JobStatusType.Cancelled
        ) {
          return false
        }
        return POLLING_INTERVAL
      },
      refetchIntervalInBackground: false,
      retry: createRetryOnServerError(3)
    })),
    enabled
  })

  const jobStatuses = results.map((result, index) => ({
    ...jobs[index],
    jobData: result.data,
    isLoading: result.isLoading,
    error: result.error,
    refetch: result.refetch,
    isJobComplete: result.data
      ? result.data.status === JobStatusType.Success || result.data.status === JobStatusType.Failure
      : false
  }))

  const allJobsComplete = jobStatuses.every(
    (job) => job.jobData?.status === JobStatusType.Success || job.jobData?.status === JobStatusType.Failure
  )

  return {
    jobStatuses,
    allJobsComplete,
    hasError,
    isLoading,
    refetchAll
  }
}
